import { useParams, Link } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { loadSeoPage, getRelatedPages, getCategoryBySlug } from '../utils/seoPageLoader';
import SEO from '../components/SEO';

function SeoPage() {
  const { slug } = useParams();
  const [page, setPage] = useState(null);
  const [loading, setLoading] = useState(true);
  const relatedPages = getRelatedPages(slug);
  const category = page ? getCategoryBySlug(page.category) : null;

  useEffect(() => {
    const data = loadSeoPage(slug);
    if (data) {
      setPage(data);
    }
    setLoading(false);
  }, [slug]);

  if (loading) {
    return (
      <div className="text-center py-16">
        <p className="text-gray-600">Loading...</p>
      </div>
    );
  }

  if (!page) {
    return (
      <div className="text-center py-16">
        <h1 className="text-2xl font-bold text-gray-900 mb-4">Page not found</h1>
        <Link to="/" className="text-gray-600 hover:text-gray-800 underline">
          Back to Home
        </Link>
      </div>
    );
  }

  return (
    <div>
      <SEO title={`${page.title} | Mood Food Recommender`} description={page.description} />
      
      <article className="bg-white rounded-xl shadow-sm border p-6 mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-4">{page.title}</h1>
        <p className="text-gray-600 mb-6 text-lg">{page.intro}</p>
        
        <div className="space-y-6">
          {page.sections.map((section, index) => (
            <section key={index}>
              <h2 className="text-2xl font-semibold text-gray-900 mb-3">
                {section.heading}
              </h2>
              {section.content && (
                <p className="text-gray-600 mb-4 leading-relaxed">{section.content}</p>
              )}
              {section.items && (
                <ul className="list-disc list-inside space-y-2 text-gray-600 ml-4">
                  {section.items.map((item, itemIndex) => (
                    <li key={itemIndex}>{item}</li>
                  ))}
                </ul>
              )}
            </section>
          ))}
        </div>

        <div className="mt-8 p-6 bg-gray-50 rounded-lg border">
          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            Try the Mood Food Recommender
          </h3>
          <p className="text-gray-600 mb-4">
            Not sure what fits your mood? Get instant food suggestions based on how you feel.
          </p>
          <Link
            to="/"
            className="inline-block bg-black text-white px-6 py-3 rounded-lg font-medium hover:bg-gray-800 transition"
          >
            Choose Your Mood
          </Link>
        </div>
      </article>

      {category && (
        <div className="mb-8">
          <Link
            to={`/category/${category.slug}`}
            className="text-gray-600 hover:text-gray-800 underline"
          >
            ← Back to {category.title}
          </Link>
        </div>
      )}

      {relatedPages.length > 0 && (
        <section className="bg-white rounded-xl shadow-sm border p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Related Guides</h2>
          <div className="grid md:grid-cols-3 gap-3">
            {relatedPages.map((related) => (
              <Link
                key={related.slug}
                to={`/guides/${related.slug}`}
                className="p-4 rounded-lg border hover:bg-gray-50 transition"
              >
                <h3 className="font-medium text-gray-900 mb-1 text-sm">
                  {related.title}
                </h3>
                <p className="text-xs text-gray-600">
                  {related.description}
                </p>
              </Link>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}

export default SeoPage;
